import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "wouter";
import { ArrowLeft, FileDown, Loader2, TriangleAlert } from "lucide-react";
import { AssessmentTabs } from "@/components/assessment/AssessmentTabs";
import { ClimateRiskReport } from "@/components/assessment/report/ClimateRiskReport";
import { ReportPreviewToolbar } from "@/components/assessment/report/ReportPreviewToolbar";
import { ResultsFilters } from "@/components/assessment/results/ResultsFilters";
import { Button } from "@/components/ui/button";
import { useAuthContext } from "@/context/AuthContext";
import { buildReport } from "@/lib/report";
import { ALIGNMENT_STATEMENT } from "@/lib/report-basis";
import { loadReportSources, type ReportSources } from "@/lib/report-data";
import { exportReportPdf } from "@/lib/report-export";
import { HORIZON_LABELS } from "@/lib/results";
import { defaultResultsFilterState, orderedHorizons, type ResultsFilterState } from "@/lib/results-filters";

function AssessmentDisclosure() {
  const { workspace } = useAuthContext();
  const [sources, setSources] = useState<ReportSources | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ResultsFilterState>(defaultResultsFilterState());
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const previewRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    loadReportSources()
      .then(setSources)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load the report"));
  }, []);

  const model = useMemo(
    () => (sources ? buildReport({ workspaceName: workspace?.name ?? "", ...sources, filters }) : null),
    [sources, filters, workspace],
  );

  async function handleExport() {
    setExporting(true);
    setExportError(null);
    try {
      await exportReportPdf(filters);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "PDF export failed.");
    } finally {
      setExporting(false);
    }
  }

  const horizonSummary = orderedHorizons(filters.horizons).map((h) => HORIZON_LABELS[h]).join(" vs ");

  return (
    <div className="flex flex-col gap-6">
      <AssessmentTabs />

      {sources && (
        <ResultsFilters filters={filters} onChange={setFilters} assets={sources.assets} stages={sources.stages} />
      )}

      <div className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div className="flex flex-col gap-0.5">
          <h1 className="text-xl font-heading font-bold leading-7">Disclosure &amp; export</h1>
          <p className="text-xs leading-4 text-muted-foreground">
            Summary output for lenders, investors and the board — {filters.scenario} · {horizonSummary}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" asChild>
            <Link href="/assessment/detailed">
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to detail
            </Link>
          </Button>
          <Button onClick={handleExport} disabled={exporting || !model?.exportGate.canExport}>
            {exporting ? (
              <>
                <Loader2 className="animate-spin" />
                Generating PDF…
              </>
            ) : (
              <>
                <FileDown />
                Download PDF
              </>
            )}
          </Button>
        </div>
      </div>

      {/* ALIGNMENT_STATEMENT verbatim — it is the reviewed wording, and it
          covers ESRS and IFRS S1 as well, which the mockup's notice drops. */}
      <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5">
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <p className="text-xs font-semibold leading-5 text-amber-800">
          Notice: scope for this version is the summary below. {ALIGNMENT_STATEMENT}
        </p>
      </div>

      {error && <p className="text-sm font-medium text-destructive">{error}</p>}

      {!error && !sources && (
        <div className="flex justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        </div>
      )}

      {exportError && (
        <p role="alert" className="text-sm font-medium text-destructive">
          {exportError}
        </p>
      )}

      {sources && model && (
        model.exportGate.canExport ? (
          <div className="flex flex-col items-center gap-4">
            <ReportPreviewToolbar
              previewRef={previewRef}
              zoom={zoom}
              onZoomChange={setZoom}
              onDownload={handleExport}
              downloadDisabled={exporting}
            />
            <p className="text-xs text-muted-foreground">
              The preview shows the report's logical pages. Exact page breaks in the PDF are decided at export.
            </p>
            <div ref={previewRef} className="w-full overflow-x-auto rounded-xl bg-muted/50 p-4">
              {/* `zoom` (not transform) so the scaled pages still take their
                  real space in the scroll container — no height compensation. */}
              <div style={{ zoom }}>
                <ClimateRiskReport model={model} variant="screen" />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 rounded-xl border border-border bg-card p-10 text-center">
            <p className="font-medium text-muted-foreground">Nothing to report yet</p>
            <p className="text-sm text-muted-foreground/70">{model.exportGate.reason}</p>
          </div>
        )
      )}
    </div>
  );
}

export default AssessmentDisclosure;
